import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Check, Sparkles } from 'lucide-react';
import { usePwaInstall } from '../../lib/usePwaInstall';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

/**
 * Install pill — a tappable gradient pill shown at the top of the hero.
 * - Running already installed → "✓ Jixxeo AI Installed"
 * - Native install prompt available → tap triggers it
 * - Otherwise → opens device-specific instructions modal
 */
export default function InstallPill() {
  const pwa = usePwaInstall();
  const [showGuide, setShowGuide] = useState(false);

  if (pwa.installed) {
    return (
      <div
        data-testid="install-pill-installed"
        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold mb-6
                   bg-emerald-500/15 border-emerald-500/40 text-emerald-300 shadow-lg shadow-emerald-500/10"
      >
        <Check className="w-3.5 h-3.5" /> Jixxeo AI Installed
      </div>
    );
  }

  const canAuto = pwa.installable;

  const onTap = async () => {
    if (canAuto) {
      const r = await pwa.promptInstall();
      if (r.outcome === 'accepted') toast.success('Installing Jixxeo AI…');
      else if (r.outcome === 'unavailable') setShowGuide(true);
    } else {
      setShowGuide(true);
    }
  };

  return (
    <>
      <button
        data-testid="install-pill"
        onClick={onTap}
        className="group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6
                   bg-gradient-to-r from-violet-600/30 via-fuchsia-500/30 to-cyan-500/30
                   border border-violet-400/40 text-violet-100 text-xs font-semibold
                   shadow-lg shadow-violet-500/20
                   hover:border-violet-300 hover:shadow-violet-500/40 transition-all
                   active:scale-95"
        aria-label="Install Jixxeo AI"
      >
        <span className="relative inline-flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-violet-200 group-hover:text-white transition-colors" />
          <span className="absolute inset-0 rounded-full bg-violet-400/30 blur-md animate-pulse group-hover:bg-violet-300/40" />
        </span>
        <span className="flex items-center gap-1.5">
          Install Jixxeo AI
          <Download className="w-3 h-3 opacity-80" />
        </span>
      </button>
      <div className="text-[11px] text-slate-500 -mt-5 mb-5 pl-1">Get quick access from your home screen</div>

      {showGuide && (
        <Dialog open onOpenChange={(o) => !o && setShowGuide(false)}>
          <DialogContent className="glass border-white/10 max-w-sm" data-testid="install-pill-guide">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display">
                <Download className="w-5 h-5 text-violet-400" />
                Add Jixxeo AI to your {pwa.platform.ios ? 'Home Screen' : 'phone'}
              </DialogTitle>
            </DialogHeader>
            {pwa.platform.ios ? (
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Make sure you're in <strong>Safari</strong>.</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Tap the <strong>Share</strong> icon.</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>Tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</span></li>
              </ol>
            ) : pwa.platform.android ? (
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Open the browser's <strong>menu</strong> (three dots).</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>Confirm — Jixxeo AI opens like a real app.</span></li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Use <strong>Chrome</strong>, <strong>Edge</strong> or <strong>Brave</strong>.</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Click the <strong>Install</strong> icon on the right of the address bar.</span></li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>Choose <strong>Install</strong>.</span></li>
              </ol>
            )}
            <Link to="/install" className="text-xs text-violet-400 hover:underline text-center mt-3 block">See all install options →</Link>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
